import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Button,
  Table,
  Modal,
  Form,
  Input,
  Space,
  InputNumber,
  Select,
  Popconfirm,
  message,
  Layout,
  Tag,
  Tabs,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import api from '../../api';
import dayjs from 'dayjs';
import { useNavigate, useLocation } from 'react-router-dom';

const { Title } = Typography;
const { Option } = Select;
const { Content } = Layout;

const Products = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isBomModalVisible, setIsBomModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
    const [search, setSearch] = useState('');
    const [categories, setCategories] = useState([]);
    const [activeTab, setActiveTab] = useState('all');
    const [stockRange, setStockRange] = useState('');
    
    const [form] = Form.useForm();
    
    // BOM States
    const [materials, setMaterials] = useState([]);
    const [bomItems, setBomItems] = useState([]);
  
    // Stock Adjustment and History States
    const [isAdjustModalVisible, setIsAdjustModalVisible] = useState(false);
      const [adjustingRecord, setAdjustingRecord] = useState(null);
      const [adjustForm] = Form.useForm();
      const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
      const [historyData, setHistoryData] = useState([]);
      const [historyPagination, setHistoryPagination] = useState({ current: 1, pageSize: 10, total: 0 });
      const [historyLoading, setHistoryLoading] = useState(false);
    
      const fetchProducts = useCallback(async (page = pagination.current, pageSize = pagination.pageSize, searchTerm = search, categoryId = null, minStock = null, maxStock = null) => {
        setLoading(true);
        try {
          const params = {
            page: page,
            pageSize: pageSize,
            search: searchTerm,
          };
          
          if (categoryId) {
            params.category_id = categoryId;
          }
          
          if (minStock !== null && minStock !== undefined) {
            params.min_stock = minStock;
          }
          
          if (maxStock !== null && maxStock !== undefined) {
            params.max_stock = maxStock;
          }
          
          const response = await api.get('/v1/products', { params });
          setData(response.data.data.map(item => ({ ...item, key: item.id })));
          setPagination({
            current: page,
            pageSize: pageSize,
            total: response.data.total,
          });
        } catch (error) {
          message.error('获取产品列表失败');
        } finally {
          setLoading(false);
        }
      }, [pagination.current, pagination.pageSize, search]);
      
      const fetchMaterials = useCallback(async () => {
        try {
            const response = await api.get('/v1/materials', { params: { limit: -1 } });
            setMaterials(response.data.data);
        } catch (error) {
            message.error('获取物料列表失败');
        }
      }, []);
      
      const fetchCategories = useCallback(async () => {
        try {
          const response = await api.get('/v1/categories', { params: { type: 2, pageSize: -1 } });
          setCategories(response.data.data);
        } catch (error) {
          message.error('获取分类列表失败');
        }
      }, []);
    
      useEffect(() => {
        // 检查是否从仪表盘传入了库存范围筛选
        if (location.state?.stockRange) {
          setStockRange(location.state.stockRange);
          const { minStock: min, maxStock: max } = parseStockRange(location.state.stockRange);
          fetchProducts(1, 10, '', null, min, max);
        } else {
          fetchProducts();
        }
        fetchMaterials();
        fetchCategories();
      }, [fetchProducts, fetchMaterials, fetchCategories, location.state]);
    
      const handleTableChange = (newPagination, filters, sorter) => {
        const categoryId = activeTab === 'all' ? null : activeTab;
        const { minStock, maxStock } = parseStockRange(stockRange);
        fetchProducts(newPagination.current, newPagination.pageSize, search, categoryId, minStock, maxStock);
      };
      
      const parseStockRange = (range) => {
        if (!range || !range.trim()) {
          return { minStock: null, maxStock: null };
        }
        // 移除所有空格后再分割
        const cleanRange = range.replace(/\s+/g, '');
        const parts = cleanRange.split('-');
        if (parts.length === 2) {
          const min = parts[0] ? parseFloat(parts[0]) : null;
          const max = parts[1] ? parseFloat(parts[1]) : null;
          return { 
            minStock: !isNaN(min) ? min : null, 
            maxStock: !isNaN(max) ? max : null 
          };
        }
        return { minStock: null, maxStock: null };
      };
      
      const handleSearch = (value) => {
        setSearch(value);
        const categoryId = activeTab === 'all' ? null : activeTab;
        const { minStock, maxStock } = parseStockRange(stockRange);
        fetchProducts(1, pagination.pageSize, value, categoryId, minStock, maxStock);
      };
      
      const handleStockFilter = () => {
        const categoryId = activeTab === 'all' ? null : activeTab;
        const { minStock, maxStock } = parseStockRange(stockRange);
        fetchProducts(1, pagination.pageSize, search, categoryId, minStock, maxStock);
      };
      
      const handleTabChange = (key) => {
        setActiveTab(key);
        const categoryId = key === 'all' ? null : key;
        const { minStock, maxStock } = parseStockRange(stockRange);
        fetchProducts(1, pagination.pageSize, search, categoryId, minStock, maxStock);
      };
    
      const showAddModal = () => {
        setEditingRecord(null);
        form.resetFields();
        setIsModalVisible(true);
      };
    
      const showEditModal = (record) => {
        setEditingRecord(record);
        form.setFieldsValue(record);
        setIsModalVisible(true);
      };
      
      const showBomModal = async (record) => {
        setEditingRecord(record);
        try {
            const response = await api.get(`/v1/products/${record.id}/bom`);
            setBomItems(response.data.map(item => ({...item, key: item.id})));
        } catch (error) {
            message.error('获取BOM列表失败');
        }
        setIsBomModalVisible(true);
      };
    
    
      const handleCancel = () => {
        setIsModalVisible(false);
        setIsBomModalVisible(false);
        setIsAdjustModalVisible(false);
        setIsHistoryModalVisible(false);
        setEditingRecord(null);
        setAdjustingRecord(null);
        setBomItems([]);
        adjustForm.resetFields();
      };
    
      const handleOk = async () => {
        try {
          const values = await form.validateFields();
          if (editingRecord) {
            await api.put(`/v1/products/${editingRecord.id}`, values);
            message.success('产品更新成功');
          } else {
            await api.post('/v1/products', values);
            message.success('产品创建成功');
          }
          setIsModalVisible(false);
          const categoryId = activeTab === 'all' ? null : activeTab;
          fetchProducts(pagination.current, pagination.pageSize, search, categoryId);
        } catch (error) {
          const errorMsg = error.response?.data?.error || (editingRecord ? '更新失败' : '创建失败');
          message.error(errorMsg);
        }
      };
      
      const handleDelete = async (id) => {
        try {
            await api.delete(`/v1/products/${id}`);
            message.success('产品删除成功');
            const categoryId = activeTab === 'all' ? null : activeTab;
            fetchProducts(pagination.current, pagination.pageSize, search, categoryId);
        } catch (error) {
            message.error('删除失败');
        }
      };
      
      // --- BOM Modal Handlers ---
      const handleSaveBom = async () => {
          try {
              const payload = bomItems.map(item => ({ material_id: item.material_id, quantity: item.quantity }));
              await api.post(`/v1/products/${editingRecord.id}/bom`, payload);
              message.success('BOM保存成功');
              setIsBomModalVisible(false);
          } catch (error) {
              message.error('BOM保存失败');
          }
      };
    
      const handleBomItemChange = (id, field, value) => {
        const newItems = bomItems.map(item => {
            if (item.id === id) {
                return { ...item, [field]: value };
            }
            return item;
        });
        setBomItems(newItems);
      };
      
      const handleAddBomItem = () => {
          const newItem = {
              id: `new-${Date.now()}`, // temp id
              material_id: null,
              quantity: 1,
          };
          setBomItems([...bomItems, newItem]);
      };
      
      const handleRemoveBomItem = (id) => {
          setBomItems(bomItems.filter(item => item.id !== id));
      };
      
      // --- Stock Adjustment and History Handlers ---
      const showAdjustModal = (record) => {
        setAdjustingRecord(record);
        adjustForm.setFieldsValue({ new_quantity: record.current_stock });
        setIsAdjustModalVisible(true);
      };
    
      const handleAdjustOk = async () => {
        try {
          const values = await adjustForm.validateFields();
          await api.post(`/v1/products/${adjustingRecord.id}/adjust-stock`, {
              new_quantity: values.new_quantity,
              note: values.note
          });
          message.success('库存调整成功');
          handleCancel();
          const categoryId = activeTab === 'all' ? null : activeTab;
          fetchProducts(pagination.current, pagination.pageSize, search, categoryId);
        } catch (error) {
          const errorMsg = error.response?.data?.error || '操作失败';
          message.error(errorMsg);
        }
      };
      
      const showHistoryModal = (record) => {
        setEditingRecord(record);
        setIsHistoryModalVisible(true);
        fetchHistory(record.id, 1, 10);
      };
    
      const fetchHistory = useCallback(async (productId, page, pageSize) => {
        if (!productId) return;
        setHistoryLoading(true);
        try {
          const response = await api.get(`/v1/products/${productId}/stock-history`, {
            params: { page, limit: pageSize },
          });
          setHistoryData(response.data.data);
          setHistoryPagination({
            current: response.data.page,
            pageSize: response.data.limit,
            total: response.data.total,
          });
        } catch (error) {
          message.error('获取库存变更记录失败');
        } finally {
          setHistoryLoading(false);
        }
      }, []);
      
      const handleHistoryTableChange = (newPagination) => {
        fetchHistory(editingRecord.id, newPagination.current, newPagination.pageSize);
      };
    
      const columns = [
        { title: '产品名称', dataIndex: 'name', key: 'name' },
        { title: '规格', dataIndex: 'spec', key: 'spec', render: (spec) => spec || '-' },
        { 
          title: '当前库存', 
          dataIndex: 'current_stock', 
          key: 'current_stock', 
          render: (stock, record) => {
            let packageCount = 0;
            if (record.specification) {
              const rawCount = stock / parseFloat(record.specification);
              const rounded = Math.floor(rawCount * 100) / 100;
              packageCount = rounded;
            }
            return (
              <span>
                {stock}{record.unit}
                {record.specification && record.package_unit && (
                  <span style={{ color: '#1890ff', marginLeft: 8 }}>
                    ({packageCount}{record.package_unit})
                  </span>
                )}
              </span>
            );
          }
        },
        { title: '标准销售价格', dataIndex: 'standard_price', key: 'standard_price', render: (price) => `¥ ${(typeof price === 'number' ? price : 0).toFixed(2)}` },
        { title: '更新时间', dataIndex: 'updated_at', key: 'updated_at' },
        {
          title: '操作',
          key: 'action',
          align: 'left',
          render: (_, record) => (
            <Space size="small" style={{ marginLeft: -8 }}>
              <Button type="link" size="small" onClick={() => showEditModal(record)}>编辑</Button>
              <Button type="link" size="small" onClick={() => showBomModal(record)}>BOM管理</Button>
              <Button type="link" size="small" onClick={() => showAdjustModal(record)}>调整库存</Button>
              <Button type="link" size="small" onClick={() => showHistoryModal(record)}>库存变更记录</Button>
              <Popconfirm
                title="确定删除此产品吗？"
                onConfirm={() => handleDelete(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button type="link" size="small" danger>删除</Button>
              </Popconfirm>
            </Space>
          ),
        },
      ];
      
        const bomColumns = [
          { 
            title: '物料名称', 
            dataIndex: 'material_id', 
            key: 'material_id',
            render: (materialId, record) => (
                <Select
                    showSearch
                    style={{ width: 200 }}
                    placeholder="选择物料"
                    optionFilterProp="children"
                    value={materialId}
                    onChange={(value) => handleBomItemChange(record.id, 'material_id', value)}
                >
                    {materials.map(m => <Option key={m.id} value={m.id}>{m.name}</Option>)}
                </Select>
            )
          },
          { 
            title: '单位产品所需数量', 
            dataIndex: 'quantity', 
            key: 'quantity', 
            render: (quantity, record) => (
                <InputNumber 
                    min={0.001} 
                    precision={3}
                    value={quantity} 
                    onChange={(value) => handleBomItemChange(record.id, 'quantity', value)} 
                />
            )
          },
          { 
            title: '操作', 
            key: 'action', 
            render: (_, record) => (
                <Button type="link" danger size="small" onClick={() => handleRemoveBomItem(record.id)}>删除</Button>
            )
          }
      ];
      
      const historyColumns = [
          { title: '变更类型', dataIndex: 'transaction_type', key: 'transaction_type', render: type => type === 'in' ? <Tag color="green">入库</Tag> : <Tag color="red">出库</Tag> },
          { 
            title: '业务类型', 
            dataIndex: 'business_type', 
            key: 'business_type',
            render: (type) => {
                const businessTypeMap = {
                    adjustment: '库存调整',
                    purchase: '采购入库',
                    production: '生产入库',
                    sales: '销售出库',
                };
                return businessTypeMap[type] || type;
            }
          },
          { title: '变更前数量', dataIndex: 'before_quantity', key: 'before_quantity' },
          { 
            title: '变更数量', 
            key: 'change_quantity',
            render: (_, record) => {
              const change = record.after_quantity - record.before_quantity;
              const color = change > 0 ? '#52c41a' : '#ff4d4f';
              const prefix = change > 0 ? '+' : '';
              return <span style={{ color }}>{prefix}{change}</span>;
            }
          },
          { title: '变更后数量', dataIndex: 'after_quantity', key: 'after_quantity' },
          { title: '关联单据/说明', dataIndex: 'reference_no', key: 'reference_no' },
          { title: '创建时间', dataIndex: 'created_at', key: 'created_at', render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm') : 'N/A' },
        ];
      
      // 构建Tab items
      const tabItems = [
        {
          key: 'all',
          label: '全部',
        },
        ...categories.map(cat => ({
          key: String(cat.id),
          label: cat.name,
        })),
      ];
    
      return (
        <Content>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Title level={4} style={{ margin: 0 }}>产品管理</Title>
            <Space>
              <Input
                placeholder="搜索产品编码或名称"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClear={() => {
                  setSearch('');
                  const categoryId = activeTab === 'all' ? null : activeTab;
                  const { minStock, maxStock } = parseStockRange(stockRange);
                  fetchProducts(1, pagination.pageSize, '', categoryId, minStock, maxStock);
                }}
                style={{ width: 200 }}
                allowClear
              />
              <Input
                placeholder="库存范围 (例如: 10-20)"
                value={stockRange}
                onChange={(e) => setStockRange(e.target.value)}
                onClear={() => {
                  setStockRange('');
                  const categoryId = activeTab === 'all' ? null : activeTab;
                  fetchProducts(1, pagination.pageSize, search, categoryId, null, null);
                }}
                style={{ width: 180 }}
                allowClear
              />
              <Button type="primary" onClick={handleStockFilter}>筛选</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={showAddModal}>
                新增产品
              </Button>
            </Space>
          </div>
          
          <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} />
          
          <Table
            columns={columns}
            dataSource={data}
            loading={loading}
            pagination={pagination}
            onChange={handleTableChange}
            size="small"
            bordered
          />
    
          <Modal
            title={editingRecord ? '编辑产品' : '新增产品'}
            open={isModalVisible}
            onOk={handleOk}
            onCancel={handleCancel}
            destroyOnClose
          >
            <Form form={form} layout="vertical" name="product_form">
              <Form.Item name="name" label="产品名称" rules={[{ required: true, message: '请输入产品名称!' }]}>
                <Input />
              </Form.Item>
              <Form.Item name="spec" label="规格">
                <Input placeholder="请输入产品规格" />
              </Form.Item>
              <Form.Item name="specification" label="每包装含量" rules={[{ required: true, message: '请输入每包装含量!' }, { pattern: /^\d+$/, message: '每包装含量必须为纯数字!' }]}>
                <Input placeholder="请输入纯数字" />
              </Form.Item>
          <Form.Item name="package_unit" label="包装单位" rules={[{ required: true, message: '请选择包装单位!' }]}>
            <Select placeholder="请选择包装单位" allowClear>
              <Option value="袋">袋</Option>
              <Option value="箱">箱</Option>
              <Option value="桶">桶</Option>
              <Option value="根">根</Option>
              <Option value="个">个</Option>
              <Option value="把">把</Option>
              <Option value="卷">卷</Option>
            </Select>
          </Form.Item>
              <Form.Item name="category_id" label="分类">
                <Select placeholder="请选择分类" allowClear>
                  {categories.map(cat => <Option key={cat.id} value={cat.id}>{cat.name}</Option>)}
                </Select>
              </Form.Item>
              <Form.Item name="unit" label="库存单位" rules={[{ required: true, message: '请输入库存单位!' }]}>
                <Input placeholder="例如：KG、L、个" />
              </Form.Item>
              <Form.Item name="standard_price" label="标准销售价格" rules={[{ required: true, message: '请输入价格!' }]}>
                <InputNumber min={0} style={{ width: '100%' }} precision={2} />
              </Form.Item>
            </Form>
          </Modal>
          
          {/* BOM Management Modal */}
          <Modal
            title={`BOM管理: ${editingRecord?.name}`}
            open={isBomModalVisible}
            onOk={handleSaveBom}
            onCancel={handleCancel}
            width={800}
            destroyOnClose
            footer={[
              <Button key="cancel" onClick={handleCancel}>取消</Button>,
              <Button key="save" type="primary" loading={loading} onClick={handleSaveBom}>保存BOM</Button>
            ]}
          >
            <Button onClick={handleAddBomItem} type="primary" icon={<PlusOutlined/>} size="small" style={{marginBottom: 16}}>添加物料</Button>
            <Table columns={bomColumns} dataSource={bomItems} size="small" bordered pagination={false} rowKey="id" />
          </Modal>
    
          {/* Adjust Stock Modal */}
          <Modal
            title={`调整库存: ${adjustingRecord?.name || ''}`}
            open={isAdjustModalVisible}
            onOk={handleAdjustOk}
            onCancel={handleCancel}
            destroyOnClose
          >
            <Form form={adjustForm} layout="vertical" name="adjust_stock_form">
                <Form.Item label="当前库存">
                    <Input value={adjustingRecord?.current_stock} disabled />
                </Form.Item>
                <Form.Item
                    name="new_quantity"
                    label="调整后库存"
                    rules={[{ required: true, message: '请输入调整后的库存数量!' }]}
                >
                    <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                    name="note"
                    label="备注"
                >
                    <Input.TextArea rows={2} placeholder="例如：盘点调整、生产批号等" />
                </Form.Item>
            </Form>
          </Modal>
          
          {/* Stock History Modal */}
          <Modal
            title={`库存变更记录: ${editingRecord?.name || ''}`}
            open={isHistoryModalVisible}
            onCancel={handleCancel}
            footer={[ <Button key="close" onClick={handleCancel}>关闭</Button> ]}
            width={1000}
            destroyOnClose
          >
            <Table
              columns={historyColumns}
              dataSource={historyData}
              rowKey="id"
              pagination={historyPagination}
              loading={historyLoading}
              onChange={handleHistoryTableChange}
              size="small"
              bordered
            />
          </Modal>
        </Content>
      );
    };
    
    export default Products;
    
  